<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  options: {
    type: Array,
    required: true
  },
  initialSelectedValue: {
    type: [Number, String],
    default: null
  },
  inputName: {
    type: String,
    required: true
  },
  searchInputId: {
    type: String,
    required: true
  },
  clearText: {
    type: String,
    required: true
  },
  noResultsText: {
    type: String,
    required: true
  },
  searchLabel: {
    type: String,
    required: true
  },
  searchPlaceholder: {
    type: String,
    required: true
  },
  resetEventTarget: {
    type: Object,
    default: null
  }
})

const root = ref(null)
const query = ref("")
const isOpen = ref(false)
const selectedValue = ref(props.initialSelectedValue ? String(props.initialSelectedValue) : "")

const selectedOption = computed(() => {
  return props.options.find((option) => String(option.value) === selectedValue.value)
})

const filteredOptions = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase()
  if (normalizedQuery === "") return props.options

  return props.options.filter((option) => {
    const searchableText = option.searchText ||
        [option.label, option.description, option.metrics].filter(Boolean).join(" ")
    return searchableText.toLowerCase().includes(normalizedQuery)
  })
})

function open() {
  isOpen.value = true
}

function close() {
  isOpen.value = false
}

function select(option) {
  selectedValue.value = String(option.value)
  query.value = option.label
  close()
}

function clear() {
  selectedValue.value = ""
  query.value = ""
  open()
}

function reset() {
  selectedValue.value = props.initialSelectedValue ? String(props.initialSelectedValue) : ""

  if (selectedOption.value) {
    query.value = selectedOption.value.label
  } else {
    selectedValue.value = ""
    query.value = ""
  }

  close()
}

function closeOnOutsideClick(event) {
  if (!root.value?.contains(event.target)) close()
}

onMounted(() => {
  reset()

  document.addEventListener("click", closeOnOutsideClick)
  resetTarget().addEventListener("searchable-dropdown:reset", reset)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
  resetTarget().removeEventListener("searchable-dropdown:reset", reset)
})

function resetTarget() {
  return props.resetEventTarget || root.value
}
</script>

<template>
  <div ref="root">
    <input type="hidden" :name="inputName" :value="selectedValue">

    <div class="relative">
      <label :for="searchInputId" class="sr-only">{{ searchLabel }}</label>
      <input
        :id="searchInputId"
        v-model="query"
        type="search"
        class="input w-full rounded-md border-base-300 bg-base-100"
        :placeholder="searchPlaceholder"
        autocomplete="off"
        @focus="open"
        @input="open"
        @keydown.esc="close"
      >

      <button
        v-if="selectedValue"
        type="button"
        class="btn btn-ghost btn-sm absolute right-2 top-1/2 min-h-0 -translate-y-1/2 rounded-md px-2 text-neutral"
        @click="clear"
      >
        {{ clearText }}
      </button>

      <div
        v-if="isOpen"
        class="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border border-base-300 bg-base-100 shadow-lg"
      >
        <button
          v-for="option in filteredOptions"
          :key="option.value"
          type="button"
          class="block w-full px-4 py-3 text-left hover:bg-secondary/10"
          @mousedown.prevent="select(option)"
        >
          <span class="block font-semibold text-primary">{{ option.label }}</span>
          <span v-if="option.description" class="block text-sm text-neutral">{{ option.description }}</span>
          <span v-if="option.metrics" class="mt-0.5 block text-xs text-neutral">{{ option.metrics }}</span>
        </button>

        <p v-if="filteredOptions.length === 0" class="px-4 py-3 text-sm text-neutral">
          {{ noResultsText }}
        </p>
      </div>
    </div>
  </div>
</template>
